import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import {
    captureErrorOnce,
    collectErrorDiagnostics,
    ErrorDiagnostics,
} from '~/utils/error-diagnostics';
import ErrorPage from './ErrorPage';

interface UnexpectedErrorPageProps {
    error: unknown;
    /** Which boundary caught the error; sent to Sentry as a tag. */
    boundary: string;
    componentStack?: string;
    /** Set when a Sentry boundary has already captured the error. */
    eventId?: string;
    status?: number;
    title?: string;
    description?: string;
}

export const UnexpectedErrorPage = ({
    error,
    boundary,
    componentStack,
    eventId,
    status = 500,
    title = 'An Unexpected Error Occurred',
    description = 'The workspace could not display this page. Changes that were not saved can be lost. Return to the start screen to continue.',
}: UnexpectedErrorPageProps) => {
    const [diagnostics, setDiagnostics] = useState<ErrorDiagnostics>();

    // React Router's errorElement stops the error before Sentry's global
    // handlers. captureConsole usually records it from React's console.error
    // log, but not always, so make sure it is captured and show the event ID
    // Sentry actually stored.
    useEffect(() => {
        const capturedEventId = captureErrorOnce(error, boundary, eventId);
        setDiagnostics(
            collectErrorDiagnostics(error, {
                eventId: capturedEventId,
                componentStack,
            })
        );
    }, [error, boundary, componentStack, eventId]);

    return (
        <ErrorPage
            status={status}
            title={title}
            description={description}
            diagnostics={diagnostics}
        />
    );
};

const RouteErrorBoundary = () => {
    const error = useRouteError();
    if (isRouteErrorResponse(error)) {
        if (error.status === 503) {
            return (
                <ErrorPage
                    status={error.status}
                    title={'Service is currently unavailable, try again later!'}
                />
            );
        }
        if (error.status === 401) {
            return (
                <ErrorPage
                    status={error.status}
                    title={'You are not authorized to see this page.'}
                />
            );
        }
        if (error.status === 404) {
            return (
                <ErrorPage
                    status={error.status}
                    title={"Couldn't find the page you were looking for."}
                />
            );
        }
    }

    return <UnexpectedErrorPage error={error} boundary="route" />;
};

export default RouteErrorBoundary;
